import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, post, del, put, upload, fileUrl } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState } from "@/components/common";
import { ChevronRight, ChevronDown, Plus, Trash2, Layers, ImagePlus } from "lucide-react";

const NEXT = { category: "exam", exam: "subject", subject: "chapter", chapter: "topic", topic: null };

function flatten(nodes, depth, open, out) {
  for (const n of nodes) {
    out.push({ node: n, depth });
    if (open[n.id] && n.children && n.children.length) flatten(n.children, depth + 1, open, out);
  }
  return out;
}

export default function Taxonomy() {
  const qc = useQueryClient();
  const [open, setOpen] = useState({});
  const [adding, setAdding] = useState(null);
  const [name, setName] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["tax-tree"], queryFn: () => get("/taxonomy/tree") });

  const onAdd = async (parent_id, level) => {
    if (!name.trim()) return;
    await post("/taxonomy", { name, level, parent_id });
    setName(""); setAdding(null); toast.success("Added");
    if (parent_id) setOpen((o) => ({ ...o, [parent_id]: true }));
    qc.invalidateQueries({ queryKey: ["tax-tree"] });
  };
  const onRemove = async (id) => {
    try { await del(`/taxonomy/${id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["tax-tree"] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete children first"); }
  };
  const onLogo = async (node, file) => {
    try {
      const r = await upload(file);
      await put(`/taxonomy/${node.id}`, { name: node.name, level: node.level, parent_id: node.parent_id || null,
        description: node.description || "", badge: node.badge || "", color: node.color || "", logo: `/api/files/${r.id}` });
      toast.success("Logo updated"); qc.invalidateQueries({ queryKey: ["tax-tree"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const rows = data ? flatten(data, 0, open, []) : [];

  return (
    <div>
      <PageHeader title="Exam Taxonomy" subtitle="Category → Exam → Subject → Chapter → Topic" action={<button onClick={() => { setAdding({ parent_id: null, level: "category" }); setName(""); }} data-testid="add-category-btn" className="bg-indigo-800 text-white font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2"><Plus className="h-4 w-4" /> Add Category</button>} />
      {adding && adding.parent_id === null && (
        <div className="flex gap-2 mb-4 bg-white p-3 rounded-xl border border-slate-200">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onAdd(null, "category")} data-testid="tax-name-input" className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1" placeholder="New category name" />
          <button onClick={() => onAdd(null, "category")} className="bg-indigo-800 text-white px-4 rounded-lg text-sm font-semibold">Add</button>
          <button onClick={() => setAdding(null)} className="text-slate-500 px-3 text-sm">Cancel</button>
        </div>
      )}
      {isLoading ? <PageLoader /> : !data || data.length === 0 ? <EmptyState icon={Layers} title="No taxonomy yet" /> : (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          {rows.map(({ node, depth }) => {
            const hasChildren = node.children && node.children.length > 0;
            const nextLevel = NEXT[node.level];
            return (
              <div key={node.id}>
                <div className="flex items-center gap-2 py-1.5 group" style={{ paddingLeft: depth * 18 }}>
                  {hasChildren ? (
                    <button onClick={() => setOpen({ ...open, [node.id]: !open[node.id] })} data-testid={`toggle-${node.id}`}>
                      {open[node.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  ) : <span className="w-4 inline-block" />}
                  {node.level === "category" && (node.logo
                    ? <img src={fileUrl(node.logo.replace("/api", ""))} alt="" className="h-6 w-6 rounded object-cover border border-slate-200" data-testid={`tax-logo-${node.id}`} />
                    : <span className="h-6 w-6 rounded bg-slate-100 inline-flex items-center justify-center text-slate-300"><ImagePlus className="h-3.5 w-3.5" /></span>)}
                  <span className="font-medium text-slate-800">{node.name}</span>
                  <span className="text-xs text-slate-400 capitalize">{node.level}</span>
                  {node.level === "category" && (
                    <label className="text-indigo-600 text-xs flex items-center gap-0.5 cursor-pointer" data-testid={`tax-logo-upload-${node.id}`}>
                      <ImagePlus className="h-3 w-3" /> logo
                      <input type="file" accept="image/*,.ico,.svg" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) onLogo(node, f); }} />
                    </label>
                  )}
                  {nextLevel && (
                    <button onClick={() => { setAdding({ parent_id: node.id, level: nextLevel }); setName(""); }} data-testid={`add-child-${node.id}`} className="opacity-0 group-hover:opacity-100 text-indigo-600 text-xs flex items-center gap-0.5">
                      <Plus className="h-3 w-3" /> {nextLevel}
                    </button>
                  )}
                  <button onClick={() => onRemove(node.id)} className="opacity-0 group-hover:opacity-100 text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                {adding && adding.parent_id === node.id && (
                  <div className="flex gap-2 my-1" style={{ paddingLeft: (depth + 1) * 18 }}>
                    <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onAdd(node.id, adding.level)} data-testid="tax-name-input" className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" placeholder={`New ${adding.level}`} />
                    <button onClick={() => onAdd(node.id, adding.level)} className="bg-indigo-800 text-white px-3 rounded-lg text-sm">Add</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
